import { format, isSameDay } from 'date-fns'
import PropTypes from 'prop-types'

const WeeklyCalendarGrid = ({
  weekDates,
  TIME_SLOTS,
  formatTimeSlot,
  isUserAvailable,
  getOpponentsAvailable,
  handleCellClick,
  allMatches,
  currentUserPlayerId,
  days,
}) => (
  <div className="calendar-grid">
    <div className="time-header">Time</div>

    {weekDates.map((date, index) => {
      const isToday = isSameDay(date, new Date())
      return (
        <div key={index} className={`day-header ${isToday ? 'today' : ''}`}>
          <div className="day-name">{days[index]}</div>
          <div className="day-date">
            {format(date, 'M/d')}
            {isToday && <span className="today-indicator">Today</span>}
          </div>
        </div>
      )
    })}

    {TIME_SLOTS.map((timeSlot, index) => (
      <div key={index} className="calendar-row">
        <div className="time-slot">{formatTimeSlot(timeSlot)}</div>

        {weekDates.map((date, dayIndex) => {
          const isUserAvail = isUserAvailable(date, timeSlot)
          const availableOpponents = getOpponentsAvailable(date, timeSlot)
          const hasOverlap = isUserAvail && availableOpponents.length > 0
          const isToday = isSameDay(date, new Date())

          let cellClass = 'calendar-cell'
          if (isToday) {
            cellClass += ' today-column'
          }
          if (hasOverlap) {
            cellClass += ' overlap-available'
          } else if (isUserAvail) {
            cellClass += ' user-available'
          } else if (availableOpponents.length > 0) {
            cellClass += ' opponent-available'
          }

          let cellContent = null
          if (availableOpponents.length > 0) {
            const prevTimeSlot = index > 0 ? TIME_SLOTS[index - 1] : null
            const prevOpponents = prevTimeSlot
              ? getOpponentsAvailable(date, prevTimeSlot)
              : []

            const newOpponents = availableOpponents.filter(
              (opp) => !prevOpponents.some((prev) => prev.id === opp.id),
            )

            if (newOpponents.length > 0) {
              const homeAwayData = newOpponents
                .map((opponent) => {
                  const matchWithOpponent = allMatches.find(
                    (m) =>
                      (m.home_player_id === currentUserPlayerId &&
                        m.away_player_id === opponent.id) ||
                      (m.away_player_id === currentUserPlayerId &&
                        m.home_player_id === opponent.id),
                  )
                  if (!matchWithOpponent) return null
                  const isHome =
                    matchWithOpponent.home_player_id === currentUserPlayerId
                  return { icon: isHome ? '🏠' : '🏃', isHome }
                })
                .filter(Boolean)

              const uniqueData = homeAwayData.filter(
                (data, dataIndex, self) =>
                  self.findIndex((d) => d.icon === data.icon) === dataIndex,
              )

              if (uniqueData.length > 0) {
                cellContent = (
                  <div className="cell-home-away-icons">
                    {uniqueData.map((data, idx) => (
                      <span
                        key={idx}
                        aria-label={data.isHome ? 'Home match' : 'Away match'}
                      >
                        {data.icon}
                      </span>
                    ))}
                  </div>
                )
              }
            }
          }

          return (
            <div
              key={`${timeSlot.hour}-${timeSlot.minute}-${dayIndex}`}
              className={cellClass}
              onClick={() =>
                handleCellClick(date, timeSlot, availableOpponents.length > 0)
              }
              title={
                hasOverlap
                  ? `You and ${availableOpponents.length} opponent${
                      availableOpponents.length > 1 ? 's' : ''
                    } available - click to view`
                  : availableOpponents.length > 0
                    ? `${availableOpponents.length} opponent${
                        availableOpponents.length > 1 ? 's' : ''
                      } available - click to view`
                    : isUserAvail
                      ? 'You are available - click to edit'
                      : 'Click to add availability'
              }
            >
              {cellContent}
            </div>
          )
        })}
      </div>
    ))}
  </div>
)

WeeklyCalendarGrid.propTypes = {
  weekDates: PropTypes.arrayOf(PropTypes.instanceOf(Date)).isRequired,
  TIME_SLOTS: PropTypes.arrayOf(
    PropTypes.shape({
      hour: PropTypes.number.isRequired,
      minute: PropTypes.number.isRequired,
    }),
  ).isRequired,
  formatTimeSlot: PropTypes.func.isRequired,
  isUserAvailable: PropTypes.func.isRequired,
  getOpponentsAvailable: PropTypes.func.isRequired,
  handleCellClick: PropTypes.func.isRequired,
  allMatches: PropTypes.array.isRequired,
  currentUserPlayerId: PropTypes.number.isRequired,
  days: PropTypes.arrayOf(PropTypes.string).isRequired,
}

export default WeeklyCalendarGrid
